import {
  getCompany,
  getPlanData,
  getAvailableYears,
  getLatestYear,
} from '../generated/dataHelpers';
import { PlanData, HealthPlan, CoverageType } from '../types';

/**
 * Load and parse plan data for a given year (synchronous, compile-time loaded)
 */
export const loadPlanData = (company: string, year: number): PlanData => {
  try {
    const planData = getPlanData(company, year);

    // Validate the structure
    if (!planData || !planData.plans || !Array.isArray(planData.plans)) {
      throw new Error('Invalid plan data structure');
    }

    return planData;
  } catch (error) {
    console.error('Error loading plan data:', error);
    throw error;
  }
};

/**
 * Get all years a company has data for, newest first
 */
export const getAvailableDataYears = (company: string): number[] => {
  return getAvailableYears(company);
};

/**
 * The year selected by default: the company's chosen year, else the most recent one
 */
export const getDefaultYear = (company: string): number => {
  const chosen = getCompany(company)?.default_year;
  return chosen && getAvailableYears(company).includes(chosen) ? chosen : getLatestYear(company);
};

/** Explainer on HSAs, including investing the balance. */
export const HSA_VIDEO_URL = 'https://www.youtube.com/watch?v=N4dPuqKn3nM';

/**
 * Get HSA contribution limits for a given year and coverage type
 */
export const getMaxHSAContribution = (
  planData: PlanData,
  coverage: CoverageType,
  ageGroup: 'under_55' | '55_plus'
): number => {
  if (!planData.hsa_contribution_limits) {
    return 0;
  }

  const limits = planData.hsa_contribution_limits;
  let maxContribution: number;

  if (coverage === CoverageType.Single) {
    maxContribution = limits.single_coverage;
  } else {
    maxContribution = limits.family_coverage;
  }

  // Add catch-up contribution for users 55 and older
  if (ageGroup === '55_plus') {
    maxContribution += limits.catch_up_age_55_plus;
  }

  return maxContribution;
};

/**
 * Get FSA contribution limits for a given year
 */
export const getMaxFSAContribution = (planData: PlanData): number => {
  return planData.fsa_contribution_limits?.healthcare_fsa || 0;
};

/**
 * Get employer HSA contribution for a specific plan and coverage type
 */
/**
 * Determine if a plan qualifies for an HSA based on IRS requirements
 */
export const isHSAQualified = (
  plan: HealthPlan,
  planData: PlanData,
  coverage: CoverageType
): boolean => {
  const limits = planData.hsa_qualification_limits;
  const deductible = plan.annual_deductible.in_network;

  // Determine which minimum deductible threshold to use
  const minDeductible =
    coverage === CoverageType.Single
      ? limits.minimum_deductible.single
      : limits.minimum_deductible.family;

  // Check if plan meets minimum deductible requirement
  if (coverage === CoverageType.Single) {
    return deductible.single >= minDeductible;
  } else {
    // For two_party and family coverage, check family deductible
    return deductible.family >= minDeductible;
  }
};

/**
 * Get employer HSA contribution for a specific plan and coverage type
 */
export const getEmployerHSAContribution = (plan: HealthPlan, coverage: CoverageType): number => {
  if (!plan.employer_hsa_contribution) {
    return 0;
  }

  return plan.employer_hsa_contribution[coverage] || 0;
};
